import { Component, Input, OnDestroy, SimpleChanges, ElementRef, ViewChild, AfterViewInit, OnChanges } from '@angular/core';
import { Chart, ChartArea, ChartConfiguration, Plugin, registerables} from 'chart.js';
import { DonutWidgetConfig } from '../../types/solution-participation-types';

@Component({
    selector: 'app-widget-gauge-chart',
    templateUrl: './widget-gauge-chart.component.html',
    styleUrls: ['./widget-gauge-chart.component.scss'],
    standalone: false
})
export class WidgetGaugeChartComponent implements OnDestroy, OnChanges, AfterViewInit {

  @ViewChild('chartCanvas') chartCanvas!: ElementRef<HTMLCanvasElement>;
  @Input() widgetType: string = "";
  @Input() data: number[] = [];
  @Input() colors: string[] = [];
  @Input() showNeedle: boolean = true;
  @Input() showLabels: boolean = true;
  @Input() showTargetLine: boolean = false;
  @Input() showMiddleText: boolean = false;
  @Input() customMiddleText: string = '';
  @Input() showSmallWidget: boolean = false;
  @Input() chartConfig : DonutWidgetConfig = {};
  @Input() complianceTarget: number = 0;

  chart: Chart<"doughnut", number[], unknown> | undefined;
  config!: DonutWidgetConfig;

  private currentRotation: number = 0;
  private animationId: number | null = null;

  private readonly animationDuration: number = 2000;

  constructor() {
    Chart.register(...registerables);
  }

  ngOnInit(): void {
    this.config = {
      responsive: true,
      maintainAspectRatio: true,
      padding: {
        top: 0,
        left: 40,
        right: 40,
        bottom: 0
      },
      ...this.chartConfig
    }
  }

  ngAfterViewInit() {
    this.initChart();
  }

  ngOnChanges(changes: SimpleChanges) {
    if(changes['data'] && !changes['data'].firstChange) {
      this.updateChart();
    }
  }

  ngOnDestroy(): void {
    if (this.chart) {
      this.chart.destroy();
    }
    if (this.animationId !== null) {
      cancelAnimationFrame(this.animationId);
      this.animationId = null;
    }
  }

  private updateChart(): void {
    // Always destroy the existing chart before creating a new one
    if (this.chart) {
      this.chart.destroy();
      this.chart = undefined;
    }

    this.initChart();
  }

  private initChart(): void {
    // Cleanup logic in case the chart was already created
    if (this.chart) {
      this.chart.destroy();
    }

    if (!this.chartCanvas || !this.chartCanvas.nativeElement.getContext('2d')) return;

    const ctx = this.chartCanvas.nativeElement.getContext('2d')!;

    const donutConfig: ChartConfiguration<'doughnut'> = {
      type: 'doughnut',
      data: {
        datasets: [
          {
            data: [this.data[0], this.data[1] - this.data[0]],
            backgroundColor: this.colors,
            borderWidth: 0
          }
        ]
      },
      options: {
        cutout: '65%',
        rotation: 270,
        circumference: 180,
        responsive: this.config.responsive,
        maintainAspectRatio: this.config.maintainAspectRatio,
        hover: { mode: null as any },
        plugins: {
          tooltip: { enabled: false },
          legend: { display: false }
        },
        layout: {
          padding: this.config.padding
        },
        animation: { duration: this.showNeedle ? 0 : 2000 }
      },
      plugins: [this.createGaugePlugin()]
    };

    this.chart = new Chart(ctx, donutConfig);
    this.animateNeedle();
  }

  /**
   *@description Create a plugin to draw the gauge needle and value
    *
    * @returns Plugin to draw the gauge needle and value
  */
  createGaugePlugin(): Plugin<"doughnut"> {
    return {
      id: 'gaugePlugin',
      afterDraw: (chart) => {
        const { ctx, data } = chart;
        const meta = chart.getDatasetMeta(0);
        if (!meta.data[0]) return;

        const xCenter = meta.data[0].x;
        const yCenter = meta.data[0].y;
        const outerRadius = (meta.data[0] as any).outerRadius;
        const innerRadius = (meta.data[0] as any).innerRadius;
        const dataPointArray = data.datasets[0].data.map((datapoint: any) => datapoint);

        // Draw min/max values
        if(this.showLabels) {
          this.drawMinMaxValues(ctx, xCenter, yCenter, outerRadius);
        }

        // Show target mark on the gauge
        if(this.showTargetLine) {
          this.drawTargetIndicator(ctx, xCenter, yCenter, outerRadius, innerRadius, dataPointArray);
        }

        // Draw needle based on boolean flag
        if (this.showNeedle){
          this.drawNeedle(ctx, xCenter, yCenter, outerRadius);
        }
      }
    };
  }

  private drawMinMaxValues(ctx: CanvasRenderingContext2D, xCenter: number, yCenter: number, radius: number): void {
    ctx.save();
    ctx.font = '14px Roboto';
    ctx.fillStyle = '#666666';
    ctx.textAlign = 'center';

    // Min value (0)
    const minX = xCenter - radius - 10;
    ctx.fillText('0', minX, yCenter + 20);

    // Current value
    const currentValue = this.data[0];
    const maxValue = this.data[1];
    const angle = ((currentValue / maxValue) * Math.PI) - Math.PI;
    const labelRadius = radius + 20;
    const valueX = xCenter + labelRadius * Math.cos(angle);
    const valueY = yCenter + labelRadius * Math.sin(angle);
    ctx.fillText(currentValue.toString(), valueX, valueY);

    // Max value (20)
    const maxX = xCenter + radius + 10;
    ctx.fillText(maxValue.toString(), maxX, yCenter + 20);

    ctx.restore();
  }

  // Function to draw the tagret indicator (Compliance Target mark in case of rebate widgets)
  private drawTargetIndicator(ctx: CanvasRenderingContext2D, xCenter: number, yCenter: number, outerRadius: number, innerRadius: number, dataPointArray: number[]): void {
    const doughnutThickness = outerRadius - innerRadius; // Get the target indiactor height
    const targetValue = this.complianceTarget;
    const angle = Math.PI / 180;

    //Get total sum
    function sumArray(arr: number[]): number {
      return arr.reduce((acc: number, curr: number) => acc + curr, 0);
    }

    const totalSum = sumArray(dataPointArray); // Get sum of the data points
    const targetPointRotation = ( targetValue / totalSum * 180) - 90;

    ctx.save();

    ctx.translate(xCenter, yCenter);
    ctx.rotate(angle * targetPointRotation);

    ctx.beginPath();
    ctx.fillStyle = '#000000';
    ctx.roundRect(0, -outerRadius - 2, 3, doughnutThickness + 4, 5);
    ctx.fill();

    ctx.restore();
  }

  private drawNeedle(ctx: CanvasRenderingContext2D, xCenter: number, yCenter: number, radius: number): void {
    const needleLength = radius;
    const needleWidth = 2;

    ctx.save();
    ctx.translate(xCenter, yCenter);
    ctx.rotate(this.currentRotation);

    // Draw needle
    ctx.beginPath();
    ctx.moveTo(-needleWidth, 0);
    ctx.lineTo(0, -needleLength);
    ctx.lineTo(needleWidth, 0);
    ctx.fillStyle = '#333333';
    ctx.fill();

    // Draw center circle
    ctx.beginPath();
    ctx.arc(0, 0, 2, 0, Math.PI * 2);
    ctx.fill();

    ctx.restore();
  }

  private animateNeedle(duration: number = this.animationDuration): void {
    if (this.animationId !== null) {
      cancelAnimationFrame(this.animationId);
    }

    const startTime = performance.now();
    const startAngle = -Math.PI / 2;
    const targetAngle = startAngle + ((this.data[0] / this.data[1]) * Math.PI);

    const animate = (currentTime: number) => {
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / duration, 1);

      const eased = 1 - Math.pow(1 - progress, 3);
      this.currentRotation = startAngle + (eased * (targetAngle - startAngle));

      if (this.chart) {
        this.chart.draw();
      }

      if (progress < 1) {
        this.animationId = requestAnimationFrame(animate);
      }
    };

    this.animationId = requestAnimationFrame(animate);
  }
}
